import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  Droplets, Plus, Minus, TrendingUp, DollarSign, Wallet,
  PieChart, Info, ExternalLink, Shield, Zap, Clock, ArrowRight
} from 'lucide-react';

const LiquidityPool = () => {
  const { isConnected, account, connectWallet } = useWeb3();
  const { user, token } = useAuth();
  const [aihAmount, setAihAmount] = useState('');
  const [ethAmount, setEthAmount] = useState('');
  const [lpAmount, setLpAmount] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  // Pool statistics (would come from smart contract in production)
  const [poolStats, setPoolStats] = useState({
    totalLiquidity: 4250000,
    aihReserve: 52500000,
    ethReserve: 1850,
    dailyVolume: 850000,
    apr: 42.5,
    totalLpTokens: 15000000,
    userLpTokens: 0,
    userShare: 0,
  });

  // Calculate estimated LP tokens
  const estimatedLpTokens = () => {
    if (!aihAmount || !ethAmount) return 0;
    const aih = parseFloat(aihAmount);
    const eth = parseFloat(ethAmount);
    // Simplified calculation - in production this would use actual reserve ratios
    return Math.sqrt(aih * eth * 2800).toFixed(2);
  };

  // Calculate user's position value
  const calculatePositionValue = () => {
    const share = poolStats.userShare / 100;
    return (poolStats.totalLiquidity * share).toFixed(2);
  };

  const handleAddLiquidity = async () => {
    if (!aihAmount || !ethAmount) {
      toast.error('Please enter amounts for both tokens');
      return;
    }
    
    setIsAdding(true);
    // Simulate transaction
    await new Promise(resolve => setTimeout(resolve, 2000));
    toast.success('Liquidity added successfully!');
    setAihAmount('');
    setEthAmount('');
    setIsAdding(false);
  };

  const handleRemoveLiquidity = async () => {
    if (!lpAmount) {
      toast.error('Please enter LP token amount');
      return;
    }
    
    setIsRemoving(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    toast.success('Liquidity removed successfully!');
    setLpAmount('');
    setIsRemoving(false);
  };

  return (
    <div className="min-h-screen pt-20 pb-12 px-6" data-testid="liquidity-pool-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 glass-panel px-4 py-2 mb-6">
            <Droplets className="text-blue-400" size={18} />
            <span className="text-blue-400 text-sm font-medium">AIH/ETH Pool</span>
          </div>
          <h1 className="font-unbounded text-4xl sm:text-5xl font-bold text-white mb-4">
            Liquidity <span className="gradient-text">Pool</span>
          </h1>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            Provide liquidity to earn trading fees and LP rewards. Your liquidity powers the AIH ecosystem.
          </p>
        </div>

        {/* Pool Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="glass-panel p-5" data-testid="stat-tvl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/60 text-sm">Total Value Locked</span>
              <DollarSign className="text-green-400" size={18} />
            </div>
            <p className="font-unbounded text-2xl font-bold text-white">
              ${(poolStats.totalLiquidity / 1000000).toFixed(2)}M
            </p>
          </div>
          <div className="glass-panel p-5" data-testid="stat-volume">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/60 text-sm">24h Volume</span>
              <TrendingUp className="text-blue-400" size={18} />
            </div>
            <p className="font-unbounded text-2xl font-bold text-white">
              ${(poolStats.dailyVolume / 1000).toFixed(0)}K
            </p>
          </div>
          <div className="glass-panel p-5" data-testid="stat-apr">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/60 text-sm">APR</span>
              <Zap className="text-[#D4AF37]" size={18} />
            </div>
            <p className="font-unbounded text-2xl font-bold text-[#D4AF37]">
              {poolStats.apr}%
            </p>
          </div>
          <div className="glass-panel p-5" data-testid="stat-share">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/60 text-sm">Your Share</span>
              <PieChart className="text-purple-400" size={18} />
            </div>
            <p className="font-unbounded text-2xl font-bold text-white">
              {poolStats.userShare}%
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Pool Info */}
          <div className="space-y-6">
            {/* Pool Reserves */}
            <div className="glass-panel p-6" data-testid="pool-reserves">
              <h3 className="font-unbounded text-xl font-bold text-white mb-6">Pool Reserves</h3>
              
              <div className="space-y-4">
                <div className="bg-[#0A1628]/80 rounded-xl p-4 border border-white/5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                        <span className="text-[#D4AF37] font-bold text-sm">AIH</span>
                      </div>
                      <span className="text-white font-medium">AIH Token</span>
                    </div>
                  </div>
                  <p className="font-unbounded text-2xl font-bold text-white">
                    {(poolStats.aihReserve / 1000000).toFixed(1)}M
                  </p>
                </div>

                <div className="bg-[#0A1628]/80 rounded-xl p-4 border border-white/5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <span className="text-blue-400 font-bold text-sm">Ξ</span>
                      </div>
                      <span className="text-white font-medium">Ethereum</span>
                    </div>
                  </div>
                  <p className="font-unbounded text-2xl font-bold text-white">
                    {poolStats.ethReserve.toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Pool Ratio */}
              <div className="mt-6 pt-4 border-t border-white/10">
                <div className="flex justify-between text-sm">
                  <span className="text-white/60">Current Rate</span>
                  <span className="text-white">1 ETH = {Math.round(poolStats.aihReserve / poolStats.ethReserve).toLocaleString()} AIH</span>
                </div>
              </div>
            </div>

            {/* Your Position */}
            <div className="glass-panel p-6" data-testid="your-position">
              <h3 className="font-unbounded text-xl font-bold text-white mb-6">Your Position</h3>
              
              {poolStats.userLpTokens > 0 ? (
                <div className="space-y-4">
                  <div className="flex justify-between py-2 border-b border-white/10">
                    <span className="text-white/60">LP Tokens</span>
                    <span className="text-white font-bold">{poolStats.userLpTokens.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-white/10">
                    <span className="text-white/60">Pool Share</span>
                    <span className="text-white font-bold">{poolStats.userShare}%</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-white/60">Position Value</span>
                    <span className="text-[#D4AF37] font-bold">${calculatePositionValue()}</span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Droplets className="mx-auto text-white/20 mb-4" size={48} />
                  <p className="text-white/60 mb-2">No liquidity provided yet</p>
                  <p className="text-sm text-white/40">Add liquidity to start earning rewards</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Add/Remove Liquidity */}
          <div className="lg:col-span-2">
            <div className="glass-panel p-6" data-testid="liquidity-actions">
              <Tabs defaultValue="add">
                <TabsList className="grid w-full grid-cols-2 bg-[#0A1628] mb-6">
                  <TabsTrigger value="add" data-testid="add-tab">
                    <Plus className="mr-2" size={16} /> Add Liquidity
                  </TabsTrigger>
                  <TabsTrigger value="remove" data-testid="remove-tab">
                    <Minus className="mr-2" size={16} /> Remove Liquidity
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="add" className="space-y-6">
                  {!isConnected ? (
                    <div className="text-center py-8">
                      <Wallet className="mx-auto text-white/20 mb-4" size={48} />
                      <p className="text-white/70 mb-4">Connect your wallet to add liquidity</p>
                      <Button 
                        onClick={() => connectWallet('metamask')}
                        className="btn-primary"
                        data-testid="connect-wallet-lp"
                      >
                        Connect Wallet
                      </Button>
                    </div>
                  ) : (
                    <>
                      {/* AIH Input */}
                      <div className="bg-[#0A1628]/80 rounded-xl p-4 border border-white/10">
                        <div className="flex justify-between mb-2">
                          <label className="text-white/60 text-sm">AIH Amount</label>
                          <span className="text-xs text-white/40">
                            Balance: {(user?.aih_tokens || 0).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <Input
                            type="number"
                            placeholder="0.0"
                            value={aihAmount}
                            onChange={(e) => setAihAmount(e.target.value)}
                            className="input-cosmic text-xl font-bold"
                            data-testid="aih-amount-input"
                          />
                          <div className="flex items-center gap-2 px-3 py-2 bg-[#D4AF37]/20 rounded-lg">
                            <span className="text-[#D4AF37] font-bold">AIH</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-center">
                        <div className="w-10 h-10 rounded-full bg-[#0A1628] border border-white/10 flex items-center justify-center">
                          <Plus className="text-white/60" size={20} />
                        </div>
                      </div>

                      {/* ETH Input */}
                      <div className="bg-[#0A1628]/80 rounded-xl p-4 border border-white/10">
                        <div className="flex justify-between mb-2">
                          <label className="text-white/60 text-sm">ETH Amount</label>
                          <span className="text-xs text-white/40">Balance: 0.00</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <Input
                            type="number"
                            placeholder="0.0"
                            value={ethAmount}
                            onChange={(e) => setEthAmount(e.target.value)}
                            className="input-cosmic text-xl font-bold"
                            data-testid="eth-amount-input"
                          />
                          <div className="flex items-center gap-2 px-3 py-2 bg-blue-500/20 rounded-lg">
                            <span className="text-blue-400 font-bold">ETH</span>
                          </div>
                        </div>
                      </div>

                      {/* Estimated Output */}
                      {aihAmount && ethAmount && (
                        <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl p-4">
                          <div className="flex justify-between mb-2">
                            <span className="text-white/60">You will receive</span>
                            <span className="text-[#D4AF37] font-bold">
                              ~{estimatedLpTokens()} LP Tokens
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-white/60">Pool share after</span>
                            <span className="text-white">~0.05%</span>
                          </div>
                        </div>
                      )}

                      <Button 
                        className="w-full btn-primary text-lg py-6"
                        onClick={handleAddLiquidity}
                        disabled={isAdding || !aihAmount || !ethAmount}
                        data-testid="add-liquidity-btn"
                      >
                        {isAdding ? 'Adding Liquidity...' : 'Add Liquidity'}
                      </Button>
                    </>
                  )}
                </TabsContent>

                <TabsContent value="remove" className="space-y-6">
                  {!isConnected ? (
                    <div className="text-center py-8">
                      <Wallet className="mx-auto text-white/20 mb-4" size={48} />
                      <p className="text-white/70 mb-4">Connect your wallet to remove liquidity</p>
                      <Button 
                        onClick={() => connectWallet('metamask')}
                        className="btn-primary"
                      >
                        Connect Wallet
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="bg-[#0A1628]/80 rounded-xl p-4 border border-white/10">
                        <div className="flex justify-between mb-2">
                          <label className="text-white/60 text-sm">LP Tokens to Remove</label>
                          <span className="text-xs text-white/40">
                            Balance: {poolStats.userLpTokens.toLocaleString()}
                          </span>
                        </div>
                        <Input
                          type="number"
                          placeholder="0.0"
                          value={lpAmount}
                          onChange={(e) => setLpAmount(e.target.value)}
                          className="input-cosmic text-xl font-bold"
                          data-testid="lp-amount-input"
                        />
                      </div>

                      {/* Percentage Quick Select */}
                      <div className="grid grid-cols-4 gap-2">
                        {[25, 50, 75, 100].map(pct => (
                          <Button
                            key={pct}
                            variant="outline"
                            className="btn-secondary"
                            onClick={() => setLpAmount(String(poolStats.userLpTokens * pct / 100))}
                          >
                            {pct}%
                          </Button>
                        ))}
                      </div>

                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 flex items-start gap-3">
                        <Info className="text-yellow-400 flex-shrink-0 mt-0.5" size={18} />
                        <div>
                          <p className="text-yellow-400 font-medium">Impermanent Loss Warning</p>
                          <p className="text-white/60 text-sm">
                            Removing liquidity may result in less tokens than deposited due to price changes.
                          </p>
                        </div>
                      </div>

                      <Button 
                        className="w-full btn-secondary text-lg py-6"
                        onClick={handleRemoveLiquidity}
                        disabled={isRemoving || !lpAmount}
                        data-testid="remove-liquidity-btn"
                      >
                        {isRemoving ? 'Removing Liquidity...' : 'Remove Liquidity'}
                      </Button>
                    </>
                  )}
                </TabsContent>
              </Tabs>
            </div>

            {/* Educational Content */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="glass-panel p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <DollarSign className="text-green-400" size={20} />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Earn Fees</p>
                  <p className="text-xs text-white/60">0.3% on every trade</p>
                </div>
              </div>
              <div className="glass-panel p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Shield className="text-blue-400" size={20} />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Non-Custodial</p>
                  <p className="text-xs text-white/60">You control your funds</p>
                </div>
              </div>
              <div className="glass-panel p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Clock className="text-purple-400" size={20} />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">No Lock-up</p>
                  <p className="text-xs text-white/60">Withdraw anytime</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiquidityPool;
